// frontend/app/components/discoveryJourneys/DiscoveryJourneyLauncher.js
//
// Index section for Discovery Journeys — the story layer above Learning Loops.
// The cards themselves are DiscoveryJourneyCard; this file is the section:
// header, states, and the responsive grid they sit in.
//
// Deliberately unlike `SummerCampJourneyLauncher`, whose catalogue is a literal
// `SUMMER_CAMP_JOURNEYS = [...]` array in the bundle matched to lessons by
// English title string. Everything here comes from the API, so seeding a new
// journey makes it appear with no code change and no rebuild.
//
// This is the one component in the folder that talks to the network, and only
// when it has to: pass `journeys` and it renders them; pass `fetchWithAuth`
// without `journeys` and it loads the list itself. The map and the mastery map
// are props-only.

import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors } from "../lessonPlans/lessonPlanTheme";
import { apiListDiscoveryJourneys } from "../../discoveryJourneys";
import DiscoveryJourneyCard from "./DiscoveryJourneyCard";
import { fill, journeyRowProgress } from "./journeyProgress";

// A journey card carries a question, a mission paragraph and a loop rail, so it
// needs more room than a lesson card before it stops being readable.
const CARD_MIN = 380;
const CARD_MAX = 560;
const GRID_GAP = 16;
const MAX_COLS = 2; // three journeys across turns a story into a tile grid
const NARROW_MAX = 720;

const DiscoveryJourneyLauncher = ({
  // Supply this and the component never touches the network.
  journeys: journeysProp = null,
  // Supply this instead and it loads the list itself, through the only path
  // with 401 refresh-and-retry.
  fetchWithAuth = null,
  board,
  chapterId,
  includeDrafts = false,
  studentEmail,
  // { [discovery_journey_id]: { loop_index, completed_loops, completed } }
  progressByJourney = {},
  onOpenJourney,
  onEditJourney,
  loading: loadingProp = false,
  error: errorProp = null,
  isDarkMode = false,
  title = "Discovery Journeys",
  subtitle = "One big question, a chain of learning loops.",
  headerRight = null,
  banner = null,
  style,
  t = (s) => s,
}) => {
  const colors = getLessonColors(isDarkMode);
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(0);
  // One object rather than three pieces of state, so a resolved fetch can never
  // be observed as "loaded but still loading" between two setState calls.
  const [remote, setRemote] = useState({ journeys: null, loading: false, error: null });

  const shouldFetch = Boolean(fetchWithAuth) && !Array.isArray(journeysProp);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let cancelled = false;
    setRemote((prev) => ({ ...prev, loading: true, error: null }));

    apiListDiscoveryJourneys(fetchWithAuth, {
      board,
      chapterId,
      includeDrafts,
      studentEmail,
    })
      .then((data) => {
        if (cancelled) return;
        setRemote({
          journeys: Array.isArray(data) ? data : data?.journeys || [],
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // A failed list is reported, never rendered as "no journeys" — an empty
        // board and an unreachable server are different facts.
        setRemote({
          journeys: [],
          loading: false,
          error: err?.message || "Discovery journeys could not be loaded.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, fetchWithAuth, board, chapterId, includeDrafts, studentEmail]);

  const journeys = Array.isArray(journeysProp) ? journeysProp : remote.journeys || [];
  const loading = loadingProp || remote.loading;
  const error = errorProp || remote.error;

  const width = measuredWidth || windowWidth;
  const isNarrow = width < NARROW_MAX;

  const progressFor = useMemo(() => {
    const map = new Map(
      journeys.map((journey) => [
        journey.id,
        journeyRowProgress(journey, progressByJourney[journey.id]),
      ])
    );
    return (journey) => map.get(journey.id);
  }, [journeys, progressByJourney]);

  const completedCount = useMemo(
    () => journeys.filter((journey) => progressFor(journey).isComplete).length,
    [journeys, progressFor]
  );

  const cols = Math.max(
    1,
    Math.min(MAX_COLS, Math.floor((width + GRID_GAP) / (CARD_MIN + GRID_GAP)))
  );
  const cellWidth =
    cols === 1
      ? "100%"
      : Math.min(CARD_MAX, Math.floor((width - GRID_GAP * (cols - 1)) / cols));

  const countLabel =
    completedCount === journeys.length
      ? fill(t("All {total} completed"), { total: journeys.length })
      : completedCount > 0
        ? fill(t("{completed} of {total} completed"), {
            completed: completedCount,
            total: journeys.length,
          })
        : fill(t(journeys.length === 1 ? "{total} journey" : "{total} journeys"), {
            total: journeys.length,
          });

  // Nothing seeded for this board — stay silent rather than showing an empty
  // shell on every dashboard.
  if (!loading && !error && journeys.length === 0) return null;

  return (
    <View
      testID="journey-launcher"
      style={style}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      <View style={styles.header}>
        <View style={[styles.headerIcon, { backgroundColor: colors.accentPrimary }]}>
          {/* The glyph sits on a solid accent, so it takes the panel colour —
              the same inversion the loop rail's numbered node uses. */}
          <MaterialCommunityIcons
            name="compass-outline"
            size={16}
            color={colors.panelBg}
          />
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t(title)}</Text>
          <Text style={[styles.headerSubtitle, { color: colors.textMuted }]}>{t(subtitle)}</Text>
        </View>
        {journeys.length > 0 || headerRight ? (
          <View
            testID="journey-header-actions"
            style={[
              styles.headerActions,
              isNarrow ? styles.headerActionsStacked : styles.headerActionsInline,
            ]}
          >
            {journeys.length > 0 ? (
              <Text style={[styles.headerCount, { color: colors.textSecondary }]}>
                {countLabel}
              </Text>
            ) : null}
            {headerRight}
          </View>
        ) : null}
      </View>

      {banner}

      {loading ? (
        <View style={styles.stateBox}>
          <ActivityIndicator size="small" color={colors.accentPrimary} />
          <Text style={[styles.stateText, { color: colors.textMuted }]}>
            {t("Loading discovery journeys…")}
          </Text>
        </View>
      ) : error ? (
        <View
          style={[
            styles.stateBox,
            { borderColor: colors.border, borderWidth: 1, borderRadius: 12 },
          ]}
        >
          <MaterialCommunityIcons name="alert-outline" size={16} color={colors.textMuted} />
          <Text style={[styles.stateText, { color: colors.textMuted }]}>
            {t("Discovery journeys could not be loaded.")}
          </Text>
        </View>
      ) : (
        <View testID="journey-grid" style={styles.grid}>
          {journeys.map((journey) => (
            <View
              key={journey.id}
              testID={`journey-cell-${journey.id}`}
              style={{ width: cellWidth }}
            >
              <DiscoveryJourneyCard
                journey={journey}
                progress={progressFor(journey)}
                colors={colors}
                isDarkMode={isDarkMode}
                onOpen={onOpenJourney}
                onEdit={onEditJourney}
                t={t}
              />
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: 10,
    marginBottom: 16,
  },
  headerIcon: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  headerText: { flex: 1, minWidth: 0 },
  headerTitle: { fontSize: 16, fontWeight: "800" },
  headerSubtitle: { fontSize: 12, marginTop: 1 },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 12 },
  headerActionsInline: { marginLeft: "auto" },
  // Below the breakpoint the actions take their own line rather than squeezing
  // the title into two words.
  headerActionsStacked: { marginLeft: 0, width: "100%" },
  headerCount: { fontSize: 12, fontWeight: "700" },

  stateBox: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 12,
    gap: 8,
  },
  stateText: { fontSize: 13, marginLeft: 8 },

  grid: { flexDirection: "row", flexWrap: "wrap", gap: GRID_GAP, alignItems: "stretch" },
});

export default DiscoveryJourneyLauncher;
